/**
 * Mock data standing in for the API until `services/api` exists.
 * Values match the design handoff prototype so screens can be compared side by side.
 */
import type { ActivityEntry, CategoryName, Customer, Product, Shop } from './types';

export const SHOP: Shop = {
  name: 'ຮ້ານ ວັດສະດຸ ພອນໄຊ',
  ownerInitial: 'ພ',
  reminderDays: 15,
};

/** The prototype is dated 31/07/2026; pin "today" so mock figures stay reproducible. */
export const MOCK_TODAY = new Date(2026, 6, 31);

export const CATEGORIES: CategoryName[] = ['ທັງໝົດ', 'ຊີມັງ', 'ເຫຼັກ', 'ຊາຍ/ຫີນ', 'ທໍ່'];

/**
 * Semantic icon key per category — not a glyph.
 *
 * These were Unicode glyphs (▦ ▧ ▬ △ ◎), which a WebView resolves against
 * whatever font happens to cover them: some Android builds render ◎ and △ with
 * emoji presentation, in colour and off-baseline. The presentation layer owns
 * the artwork now, and this package stays free of it — which is also what lets
 * the back office and the admin UI share these names.
 */
export type CategoryIcon = 'grid' | 'bag' | 'bar' | 'aggregate' | 'pipe';

export const CATEGORY_ICONS: Record<string, CategoryIcon> = {
  ທັງໝົດ: 'grid',
  ຊີມັງ: 'bag',
  ເຫຼັກ: 'bar',
  'ຊາຍ/ຫີນ': 'aggregate',
  ທໍ່: 'pipe',
};

export const CATALOG: Product[] = [
  {
    id: 'p1',
    name: 'ຊີມັງ ຕີໂກ້',
    cat: 'ຊີມັງ',
    units: [
      { unit: 'ຖົງ', price: 55000, step: 1, perBase: 1 },
      // 50kg bags, so a tonne is 20 of them. Stock is held in ຖົງ; without
      // perBase a sale of 0.5 ໂຕນ would decrement half a bag instead of ten.
      { unit: 'ໂຕນ', price: 1050000, step: 0.5, perBase: 20 },
    ],
    cost: 48000,
    remaining: 120,
    lowStockAt: 20,
  },
  {
    id: 'p2',
    name: 'ເຫຼັກເສັ້ນ 12mm',
    cat: 'ເຫຼັກ',
    units: [
      { unit: 'ເສັ້ນ', price: 45000, step: 1, perBase: 1 },
      { unit: 'ໂຕນ', price: 2350000, step: 0.5, perBase: 52 },
    ],
    cost: 39000,
    remaining: 14,
    lowStockAt: 20,
  },
  {
    id: 'p3',
    name: 'ດິນຊາຍ',
    cat: 'ຊາຍ/ຫີນ',
    units: [{ unit: 'ຄິວ', price: 90000, step: 0.5 }],
    remaining: 60,
    lowStockAt: 10,
  },
  {
    id: 'p4',
    name: 'ຫີນແມ່ນ້ຳ',
    cat: 'ຊາຍ/ຫີນ',
    units: [{ unit: 'ຄິວ', price: 110000, step: 0.5 }],
    remaining: 42,
    lowStockAt: 10,
  },
  {
    id: 'p5',
    name: 'ທໍ່ PVC 3 ນິ້ວ',
    cat: 'ທໍ່',
    units: [{ unit: 'ເສັ້ນ', price: 38000, step: 1 }],
    cost: 31000,
    remaining: 8,
    lowStockAt: 15,
  },
];

export const CUSTOMERS: Customer[] = [
  { id: 'c1', name: 'ຊ່າງ ຄຳ', phone: '020 5512 3480', balance: 4200000, limit: 5000000, days: 47 },
  { id: 'c2', name: 'ຜູ້ຮັບເໝົາ ວິໄລ', phone: '020 9920 1177', balance: 3850000, limit: 6000000, days: 22 },
  { id: 'c3', name: 'ທ້າວ ສົມສັກ', phone: '020 2233 8890', balance: 1250000, limit: 3000000, days: 8 },
];

export const RECENT_ACTIVITY: ActivityEntry[] = [
  {
    id: 'a1',
    customerName: 'ຊ່າງ ຄຳ',
    detail: 'ຂາຍຕິດ / ຊີມັງ 20 ຖົງ',
    kind: 'credit',
    amount: 1100000,
  },
  {
    id: 'a2',
    customerName: 'ນາງ ບົວ',
    detail: 'ຈ່າຍເລີຍ / QR ທະນາຄານ',
    kind: 'paid',
    amount: 350000,
  },
  {
    id: 'a3',
    customerName: 'ທ້າວ ສົມສັກ',
    detail: 'ຈ່າຍເລີຍ / ເງິນສົດ',
    kind: 'paid',
    amount: 480000,
  },
];

/**
 * Today's headline figures. ຍອດຂາຍ counts credit sales too;
 * ເງິນເຂົ້າ is money actually received; ຕິດໜີ້ໃໝ່ is the difference.
 */
export const TODAY_STATS = {
  sales: 2450000,
  received: 1830000,
  get newCredit() {
    return this.sales - this.received;
  },
};

// CHASE_PROGRESS and WEEK_TREND used to live here and fed the hero ring and
// sparkbar directly. They were the last invented figures on the screen the
// owner trusts most, and with the rest of the app showing real data they read
// as a claim: a shop with nothing recorded was told it had chased 60% of its
// debt. `chaseProgress` in debt.ts and `recentTrend` in journal.ts compute both
// from the ledger, and return nothing when there is nothing to say.

/** Total outstanding across all 24 credit customers (only 3 are listed in the mock). */
export const TOTAL_DEBT = 18500000;
export const DEBTOR_COUNT = 24;

// INVOICE_SEQ_START, formatInvoiceNo and nextInvoiceNo used to number bills
// here, back when each client held its own sequence. `ledger.service._invoice_no`
// does it now — one row lock, one sequence per shop, so two people ringing up at
// the same moment cannot print the same number — and the client prints whatever
// the server answered. Nothing called them any more, and a client-side bill
// numberer left in the toolbox is an invitation to start minting numbers in a
// browser again.

let idSeq = 0;

/**
 * Random salt, minted once per JS context.
 *
 * `crypto.randomUUID` is deliberately not used: it is `undefined` outside a
 * secure context, so it works on https://pos.k2tech.la and throws when the app
 * is opened over http://192.168.x.x during LAN testing — silently, and at
 * checkout. `getRandomValues` exists in every browser and in Hermes.
 */
const sessionSalt = (() => {
  // Typed structurally rather than as the DOM's `Crypto`: this package compiles
  // without the DOM lib so it can be shared by the web app, the native shell
  // and any future Node tooling unchanged.
  const g = (globalThis as { crypto?: { getRandomValues?<T>(a: T): T } }).crypto;
  if (g?.getRandomValues) {
    const bytes = g.getRandomValues(new Uint8Array(4));
    return Array.from(bytes, (b: number) => b.toString(36).padStart(2, '0')).join('');
  }
  return Math.random().toString(36).slice(2, 8);
})();

/**
 * Unique local id. A bare `Date.now()` is not enough — adding several template
 * products in one tap runs inside a single millisecond and would hand them all
 * the same id, colliding React keys and the per-product cart lookups.
 *
 * The counter alone is not enough either, now that the UI is a web app: it is
 * per-JS-context, so a browser tab and the WebView shell — same origin, same
 * IndexedDB — both start at 0 and mint identical ids for different sales. The
 * salt is what separates them.
 *
 * The timestamp keeps ids roughly sortable. When the API lands these become
 * client-generated ids for idempotent sync.
 */
export function uid(prefix: string): string {
  idSeq += 1;
  return `${prefix}${Date.now().toString(36)}-${sessionSalt}-${idSeq.toString(36)}`;
}

/** The per-context salt, so callers can prefix device-scoped sequences with it. */
export function contextSalt(): string {
  return sessionSalt;
}
